//
//	+-----------------------------------------------+
//	| App-specific data model objects               |
// 	+-----------------------------------------------+

/**
 * @file models.js
 * @desc This contains application-specific model objects that encapsulate the
 * results of the various requests made to the WebFOCUS and/or JSON backends.
 * @author Geoffrey Pursell
 */

define('models',["underscore", "underscore.string", "backbone", "rsvp", "bloodhound",
        "cardboard/models", "cardboard/collections", "cardboard/util"
    ],
    function (_, _s, Backbone, RSVP, Bloodhound,
        CardboardModels, CardboardCollections, Util) {

        console.log("MODULE: models");

        var Promise = RSVP.Promise;
        /**
         * @exports models
         * @desc RC model objects: Contains the model objects that describe
         * underlying data sources in SLEDS
         */
        var Models = {
            defaultOrg: {
                "name": "Statewide",
                "type": "99",
                "value": "Statewide",
                "orgID": "999999000",
                "isCharterDistrict": false,
                "tokens": [
                    "statewide"
                ],
                "schoolCount": 775,
                "groupType": "state"
            }
        };
        /** this is the new function we use to sort typeahead results. It depends on
         * our patched version of typeahead.js. Watch out!
         * @function datumSorterFunction
         * @private
         * @param {Object} a - first datum
         * @param {Object} b - second datum
         * @param {Object} queryInfo - an object containing the query in both
         * string and tokenized forms */
        function datumSorterFunction(a, b, queryInfo) {
            // normalize everything to lower case
            var schoolNameA = a.name.toLowerCase(),
                schoolNameB = b.name.toLowerCase(),
                aPriority = false,
                bPriority = false,
                query = queryInfo.tokens[0].toLowerCase();

            if (_s.startsWith(schoolNameA, query)) {
                aPriority = true;
            }
            if (_s.startsWith(schoolNameB, query)) {
                bPriority = true;
            }

            // if they're both priority, do alphabetical sort
            if (aPriority && bPriority) {
                if (schoolNameA < schoolNameB) {
                    return -1;
                } else if (schoolNameA > schoolNameB) {
                    return 1;
                } else {
                    return 0;
                }
            }

            // if they're not both, it's easy.
            if (aPriority) {
                return -1;
            }
            if (bPriority) {
                return 1;
            }

            // if neither, do nothing
            return 0;
        }

        /**
         * @class
         * @classdesc SLEDSReports class; it's the Reports class plus a little patch
         * @augments CardboardModels.DocumentModel
         */
        Models.ECLDSReports = CardboardModels.DocumentModel.extend( /** @lends module:models.ECLDSReports.prototype */ {
            /** find a report's model object, given either that model,
             * its numeric ID, or its url name */
            findReport: function (report) {
                var reportsCollection = this.get("reports");
                if (_.isString(report)) {
                    var numericID = parseInt(report, 0);
                    if (_.isNaN(numericID)) {
                        // it's the "url" string identifier, cool.
                        return reportsCollection.findWhere({
                            url: report
                        });
                    } else {
                        // it's the numeric id, let's run with it
                        return reportsCollection.get(numericID);
                    }
                } else {
                    // I guess we were passed a report model! Freebie.
                    return report;
                }
            },
            getNestedCollection: function (nestedKey, nestedValue, nestedOptions) {
                switch (nestedKey) {
                    case "reports":
                        return new CardboardCollections.DocumentCollection(nestedValue, {
                            model: CardboardModels.Report
                        });
                    default:
                        return new Backbone.DocumentCollection(nestedValue, nestedOptions);
                }
            }
        });

        /**
         * @class
         * @classdesc ECLDSOrgList class; handle all the fooling around and
         * splitting and all kids of things like a normal data model should. Most of
         * this machinery used to live in the CommonDataSourceManager.
         * @augments CardboardModels.DocumentModel
         */
        Models.ECLDSOrgList = CardboardModels.Model.extend( /** @lends module:models.ECLDSOrgList.prototype */ {
            defaultOrg: Models.defaultOrg,
            /** override Cardboard's default fetch with one that automatically does
             * all the post-processing we need.
             * @returns {Object} a promise that resolves when the data is fetched
             * AND post-processed. */
            fetch: function () {
                var self = this;

                // return a promise
                return CardboardModels.Model.prototype.fetch.apply(this, arguments)
                    .then(function () {
                        var orgs = self.unfilteredOrgList = self.toJSON();

                        // set up the empty datum buckets. these are what the
                        // typeahead will actually grab on to. the common data
                        // source manager will filter them if necessary and mantain
                        // its own filterd lists, but for efficiency, we build
                        // arrays for it as we add info to the main data structure
                        // rather than converting the hashes to arrays after the
                        // fact.
                        self.unfilteredSchools = [];
                        self.unfilteredGroups = [];

                        // process them to get them ready to become typeahead datums
                        self.makeGroupDatums(orgs.groups);
                        self.makeSchoolDatums(orgs.districts);

                        // aaaand, make the bloodhounds
                        if (!self.bloodhoundsCreated) {
                            self._makeBloodhounds();
                            return self._initBloodhounds();
                        }
                        return Promise.resolve();
                        //  return self.rawOrgList;
                    })
                    .catch(Util.reportPromiseException);
            },

            // BEGIN BROUGHT IN FROM MANAGER

            /** initialize bloodhound matching engines.
             * @private
             * @returns {Promise}
             */
            _initBloodhounds: function () {
                var promises = [
                    Promise.cast(this.schoolsBloodhound.initialize()),
                    Promise.cast(this.districtsBloodhound.initialize()),
                    Promise.cast(this.countiesBloodhound.initialize()),
                    Promise.cast(this.stateBloodhound.initialize()),
                    Promise.cast(this.programsBloodhound.initialize()),
                    Promise.cast(this.charterAuthorizersBloodhound.initialize())

                ];

                return Promise.all(promises);
            },

            // PROCESSING METHODS
            /** create bloodhound matching engines. After this, they still must be
             * initialized before use.
             * @private
             */
            _makeBloodhounds: function () {
                // schools
                window.schoolsBloodhound = this.schoolsBloodhound = new Bloodhound({
                    local: this.unfilteredSchools || [],
                    limit: 10,
                    sorter: datumSorterFunction,
                    datumTokenizer: function (datum) {
                        return datum.tokens;
                    },
                    queryTokenizer: Bloodhound.tokenizers.whitespace
                });

                // counties
                window.countiesBloodhound = this.countiesBloodhound = new Bloodhound({
                    local: this.unfilteredGroups.county || [],
                    limit: 10,
                    datumTokenizer: function (datum) {
                        return datum.tokens;
                    },
                    queryTokenizer: Bloodhound.tokenizers.whitespace
                });

                // districts
                window.districtsBloodhound = this.districtsBloodhound = new Bloodhound({
                    local: this.unfilteredGroups.district || [],
                    limit: 10,
                    sorter: datumSorterFunction,
                    datumTokenizer: function (datum) {
                        return datum.tokens;
                    },
                    queryTokenizer: Bloodhound.tokenizers.whitespace
                });

                // programs
                window.programsBloodhound = this.programsBloodhound = new Bloodhound({
                    local: this.unfilteredGroups.program || [],
                    limit: 10,
                    sorter: datumSorterFunction,
                    datumTokenizer: function (datum) {
                        return datum.tokens;
                    },
                    queryTokenizer: Bloodhound.tokenizers.whitespace
                });

                // charter authorizers
                window.charterAuthorizersBloodhound = this.charterAuthorizersBloodhound = new Bloodhound({
                    local: this.unfilteredGroups.charterAuthorizer || [],
                    limit: 10,
                    sorter: datumSorterFunction,
                    datumTokenizer: function (datum) {
                        return datum.tokens;
                    },
                    queryTokenizer: Bloodhound.tokenizers.whitespace
                });
                
                window.consortiumBloodhound = this.consortiumBloodhound = new Bloodhound({
                    local: this.unfilteredGroups.consortium || [],
                    limit: 10,
                    sorter: datumSorterFunction,
                    datumTokenizer: function (datum) {
                        return datum.tokens;
                    },
                    queryTokenizer: Bloodhound.tokenizers.whitespace
                });
                // statewide bucket
                window.stateBloodhound = this.stateBloodhound = new Bloodhound({
                    local: this.unfilteredGroups.state || [],
                    limit: 10,
                    sorter: datumSorterFunction,
                    datumTokenizer: function (datum) {
                        return datum.tokens;
                    },
                    queryTokenizer: Bloodhound.tokenizers.whitespace
                });

                this.bloodhoundsCreated = true;
            },

            initBloodhounds: function () {
                var CD = CommonDataSources.Derivative;

                return Promise.all([
                    Promise.cast(CD.schoolsBloodhound.initialize()),
                    Promise.cast(CD.districtsBloodhound.initialize()),
                    Promise.cast(CD.stateBloodhound.initialize()),
                    Promise.cast(CD.charterAuthorizersBloodhound.initialize()),
                    Promise.cast(CD.programsBloodhound.initialize()),
                    Promise.cast(CD.countiesBloodhound.initialize())
                ]);
            },
            _makeSchoolDatums: function (schoolsHash) {
                var self = this;

                _(schoolsHash).each(function (school, orgID) {
                    // add some things
                    school.orgID = orgID;
                    school.tokens = self.makeTokens(school, ["name"]);
                    school.tokens.push(orgID + "");

                    self.unfilteredSchools.push(school);
                });
            },

            _makeGroupDatums: function (groupsHash) {
                var self = this;

                // loop over each group TYPE
                _(groupsHash).each(function (groupList, groupType) {

                    var thisGroupList = self.unfilteredGroups[groupType] = [];

                    // now, loop over each group in this group list
                    _(groupList).each(function (group, orgID) {
                        // add some stuff!
                        group.orgID = orgID;
                        group.groupType = groupType;
                        group.tokens = self.makeTokens(group, ["name"]);
                        group.tokens.push(orgID + "");

                        thisGroupList.push(group);
                    });
                });
            },

            makeSchoolDatums: function (schoolsHash) {

                _(schoolsHash).each(function (school, orgID) {
                    var tokens = this.makeTokens(school, "name"),
                        groups = this.getGroupsForSchool(school),
                        prettyName = Util.Data.prettifyName(school.name),
                        districtName;

                    // increment school count for the district or other group
                    _(groups).each(function (group) {
                        // increment school count
                        group.schoolCount = group.schoolCount + 1;

                        // add the group's name to the tokens array
                        if (group.groupType !== "state") {
                            tokens.push(group.name);
                        }

                        if (group.groupType === "district") {
                            districtName = group.name;
                        }

                        if (group.groupType === "county") {
                            districtName = group.name;
                        }

                    });

                    // add some things
                    _.extend(school, {
                        orgID: orgID, // unnecessary?
                        value: prettyName,
                        name: prettyName,
                        tokens: tokens,
                        districtName: districtName
                    });
                    school.tokens.push(orgID + "");

                    this.unfilteredSchools.push(school);

                }.bind(this));
            },

            /** create our internal array of group datums in advance. called by
             * fetch().  NOTE: in SLEDS, we currently have no need to re-filter
             * these lists when the report changes. In RC, it was once the case the
             * we would re-filter the list on the client side, using data sent along
             * with the list itself, but this became untenable, and we must now
             * reload the list for each report. Each report should contain an "orgs"
             * filter source.
             * @private */
            makeGroupDatums: function (groupsHash) {
                var self = this;

                // loop over each group TYPE
                _(groupsHash).each(function (groupList, groupType) {

                    var thisGroupList = self.unfilteredGroups[groupType] = [];

                    // now, loop over each group in this group list
                    _(groupList).each(function (group, orgID) {
                        var prettyName = Util.Data.prettifyName(group.name);

                        _.extend(group, {
                            value: prettyName,
                            name: prettyName,
                            orgID: orgID,
                            isCharterDistrict: (group.type == 7),
                            tokens: self.makeTokens(group, ["name"]),
                            schoolCount: 0,
                            groupType: groupType
                        });

                        // add some stuff!
                        group.tokens.push(orgID + "");

                        thisGroupList.push(group);

                    });
                });
            },

            // INFO METHODS

            getStateOrg: function () {
                var stateGroup = this.unfilteredOrgList.groups.state,
                    values = _.values(stateGroup),
                    stateOrg = values[0];

                return stateOrg;
            },

            /*  getDatumFromOrgID: function (orgID) {
                  return this.unfilteredOrgList.districts[orgID] ||
                      _(this.unfilteredOrgList.groups)
                      .find(function (group) {
                          if (group[orgID]) {
                              return true;
                          }
                          return false;
                      })[orgID];
              },*/

            getDatumFromOrgID: function (orgID) {
                var districtDatum, groupDatum;

                if (this.unfilteredOrgList.districts) {
                    districtDatum = this.unfilteredOrgList.districts[orgID];
                }

                if (this.unfilteredOrgList.groups) {
                    groupDatum = this.getGroupByOrgID(orgID);
                }

                return districtDatum || groupDatum;
            },

            /** given an orgID, find a group with that orgID. this exists to
             * abstract away the detail that we store multiple group lists.
             * @param {String} orgID - the orgID of the requested group datum object
             * @returns {Object} the group datum requested */
            getGroupByOrgID: function (orgID) {
                var groupContainingOrgID = _(this.unfilteredOrgList.groups)
                    .find(function (group) {
                        if (group[orgID]) {
                            return true;
                        }
                        return false;
                    });
                if (groupContainingOrgID) {
                    return groupContainingOrgID[orgID];
                } else {
                    return null;
                }
            },

            getDistrictForSchool: function (school) {
                var districtOrgID = school.groups.district;
                return (districtOrgID ? this.unfilteredOrgList.groups.district[districtOrgID] : null);
            },

            /** returns an array of the group structures that a school claims it
             * belongs to. usually, this is a district, but can be other things. all
             * school implicitly belong to the "state" group. */
            getGroupsForSchool: function (school) {
                var allGroups = this.unfilteredOrgList.groups,
                    groupArray = _(school.groups).map(function (groupID, groupType) {
                        //  while (groupID.length < 7)
                        //    groupID += " ";
                        return allGroups[groupType][groupID];
                    }).value();

                // add the state, every school is always in the state. right?
                groupArray.push(this.getStateOrg());

                return groupArray;
            },

            // UTILITY METHODS and PROPERTIES

            tokenizeRE: /[\s\-_\/]+/,

            tokenizeText: function (str) {
                return $.trim(str).toLowerCase().split(this.tokenizeRE);
            },

            /** this takes a list of value fields, and contructs an array of tokens
             * for typeahead matching.
             * @param {Object} input - some JSON object containing the source data fields
             * @param {String|String[]} valueFields - the fields from which to create
             * tokens. can be an array or single string.
             * @returns {Array} - a de-duplicated array of tokens */
            makeTokens: function (object, valueFields) {
                var tokens = [],
                    vfs = _(valueFields).isArray() ? valueFields : [valueFields];

                _(vfs).each(function (valueField) {
                    tokens = tokens.concat(this.tokenizeText(object[valueField]));
                }.bind(this));

                return _(tokens).value();
            }
        });

        /**
         * @class
         * @classdesc ECLDScontactInformation class; Formats incoming school information
         * data as pluginHelper.addOrgDetailPane expects it. 
         * @augments CardboardModels.DocumentModel
         */
        Models.ECLDScontactInformation = CardboardModels.Model.extend( /** @lends module:models.ECLDSOrgList.prototype */ {

            fetch: function () {
                var self = this;
                //Quick and dirty remapping of orgId to HSGRAD_ORGID
                this.params.HSGRAD_ORGID = this.params.orgId;
                CardboardModels.Model.prototype.fetch.apply(this, arguments)

            },

            /** override Cardboard's default parse method 
             *  returns the expected model format */
            parse: function (incoming) {
                CardboardModels.Model.prototype.parse.apply(this, arguments);
                var parsedData = incoming;

                if (!incoming.errorMessage) {
                    parsedData = _.flatten(incoming.contactInformation)[0];
                    if (parsedData.parent) { //Compress the parents array to an object
                        parsedData.parent = parsedData.parent[0];
                    }
                }
                return parsedData;
            }
        });

        return Models;
    });
